/**
 * WG-Tagesumsatz-Bericht: klassisches Format aus dem alten Tool.
 *
 *   Filiale · Monat
 *   ── ──────────────────────────────────────────────────
 *   Datum        | WG.1  €  %  | WG.2  €  %  | ... | Σ
 *   01.05., Fr.  | 128.75 1.7% | 667.80 8.7% | ... |
 *   02.05., Sa.  | ...
 *   Woche (KW 18)| 393.75 1.5% | 2.465.46 9.7%| ... |
 *   ...
 *   Mai 2026     | B: 2.484.00 | N: 2.321.50 | 7%: 162.50 | 19%: ...
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useAvailableStores } from '../lib/useAvailableStores.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { DateRangePicker } from '../components/DateRangePicker.tsx';
import { Icon } from '../components/Icon.tsx';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.ts';
import { TableSkeleton } from '../components/Skeleton.tsx';

const WT_SHORT = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONAT_NAMES = [
  '',
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

function startOfMonthIso(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().substring(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().substring(0, 10);
}
function fmtDateShort(iso: string): string {
  // 01.05.26
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y.slice(2)}`;
}

export function WgTagesumsatzPage() {
  const availableStores = useAvailableStores();
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const removeFilter = useFilterStore((s) => s.removeFilter);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [periode, setPeriode] = useState({
    min: startOfMonthIso(),
    max: todayIso(),
  });

  // Sync mit globalem Filter-Store (Sidebar/Chips):
  // wenn user Mix Markt 52 in der Sidebar wählt, soll der Bericht das übernehmen.
  useEffect(() => {
    if (availableStores.length === 0) return;
    // Höchste Priorität: store_name aus Filter-Store
    const sn = filters.store_name;
    if (Array.isArray(sn) && sn.length > 0) {
      const match = availableStores.find((s) => s.name === String(sn[0]));
      if (match && match.id !== storeId) {
        setStoreId(match.id);
        return;
      }
    }
    const nr = filters.store_number;
    if (Array.isArray(nr) && nr.length > 0) {
      const match = availableStores.find((s) => s.store_number === String(nr[0]));
      if (match && match.id !== storeId) {
        setStoreId(match.id);
        return;
      }
    }
    // Default: erste Filiale wenn nichts gewählt
    if (storeId === null) setStoreId(availableStores[0].id);
  }, [availableStores, filters.store_name, filters.store_number, storeId]);

  // Datum-Filter aus Sidebar (falls gesetzt) → Periode-Range übernehmen
  useEffect(() => {
    const d = filters.datum;
    if (d && !Array.isArray(d)) {
      const min = d.min ? String(d.min) : periode.min;
      const max = d.max ? String(d.max) : periode.max;
      if (min !== periode.min || max !== periode.max) {
        setPeriode({ min, max });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.datum]);

  // Wenn der User die Filiale im Dropdown ändert, im globalen Filter-Store
  // entsprechend setzen, damit der Chip oben aktuell ist.
  function selectStore(newId: number) {
    setStoreId(newId);
    const store = availableStores.find((s) => s.id === newId);
    if (store) {
      // Alle vorherigen store_name/_number-Filter ersetzen
      removeFilter('store_name');
      removeFilter('store_number');
      addFilter('store_name', store.name);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['wg-tagesumsatz', storeId, periode],
    queryFn: () => api.wgTagesumsatz({ store_id: storeId!, periode }),
    enabled: storeId !== null && !!periode.min && !!periode.max,
  });

  const selectedStore = availableStores.find((s) => s.id === storeId);

  // Wochengrenzen: nach welchem Day-Index kommt eine Wochen-Subsummen-Zeile?
  type WeekRow = NonNullable<typeof data>['weeks'][number];
  const weekBoundaries = useMemo(() => {
    const map = new Map<number, WeekRow>();
    if (!data) return map;
    for (const w of data.weeks) {
      let lastIdx = -1;
      data.days.forEach((d, i) => {
        if (d.kw === w.kw && Number(d.datum.substring(0, 4)) === w.jahr) lastIdx = i;
      });
      if (lastIdx >= 0) map.set(lastIdx, w);
    }
    return map;
  }, [data]);

  // Pro Tag: welche WG hat den höchsten Brutto-Anteil (=visuelles Highlight)
  const dayMaxWg = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const d of data.days) {
      let maxId = '';
      let maxV = -Infinity;
      for (const [id, v] of Object.entries(d.wg_values)) {
        if (v.brutto > maxV) {
          maxV = v.brutto;
          maxId = id;
        }
      }
      if (maxId) map.set(d.datum, maxId);
    }
    return map;
  }, [data]);

  // Pro Woche: stärkste WG der KW
  const weekMaxWg = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const w of data.weeks) {
      let maxId = '';
      let maxV = -Infinity;
      for (const [id, v] of Object.entries(w.wg_values)) {
        if (v.brutto > maxV) {
          maxV = v.brutto;
          maxId = id;
        }
      }
      if (maxId) map.set(`${w.jahr}-${w.kw}`, maxId);
    }
    return map;
  }, [data]);

  // Top-3-WG der gesamten Periode (für Spalten-Akzent)
  const topWgIds = useMemo(() => {
    if (!data) return new Set<string>();
    const sorted = Object.entries(data.summary.wg_values)
      .sort((a, b) => b[1].brutto - a[1].brutto)
      .slice(0, 3)
      .map((e) => e[0]);
    return new Set(sorted);
  }, [data]);
  const topWgId = useMemo(() => {
    if (!data) return '';
    let id = '';
    let max = -Infinity;
    for (const [k, v] of Object.entries(data.summary.wg_values)) {
      if (v.brutto > max) {
        max = v.brutto;
        id = k;
      }
    }
    return id;
  }, [data]);

  const periodTitle = useMemo(() => {
    if (!periode.min) return '';
    const [y, m] = periode.min.split('-');
    return `${MONAT_NAMES[Number(m)]} ${y}`;
  }, [periode.min]);

  function exportCsv() {
    if (!data) return;
    const head = [
      'Datum',
      ...data.warengruppen.flatMap((wg) => [`${wg.name} €`, `${wg.name} %`]),
      'Tag Σ',
    ].join(';');
    const lines = [head];
    data.days.forEach((d, i) => {
      const cells: string[] = [];
      cells.push(`${fmtDateShort(d.datum)} ${WT_SHORT[d.wochentag] ?? ''}`);
      for (const wg of data.warengruppen) {
        const v = d.wg_values[wg.id];
        cells.push(v ? String(v.brutto.toFixed(2)) : '');
        cells.push(v ? String((v.anteil_pct * 100).toFixed(1)) : '');
      }
      cells.push(d.tag_total.toFixed(2));
      lines.push(cells.join(';'));
      // Wochen-Subsumme
      const w = weekBoundaries.get(i);
      if (w) {
        const wCells: string[] = [`Woche KW${w.kw}`];
        for (const wg of data.warengruppen) {
          const v = w.wg_values[wg.id];
          wCells.push(v ? v.brutto.toFixed(2) : '');
          wCells.push(v ? (v.anteil_pct * 100).toFixed(1) : '');
        }
        wCells.push(w.week_total.toFixed(2));
        lines.push(wCells.join(';'));
      }
    });
    // Monats-Summary
    const sCells: string[] = [periodTitle];
    for (const wg of data.warengruppen) {
      const s = data.summary.wg_values[wg.id];
      sCells.push(s ? `B:${s.brutto.toFixed(2)} N:${s.netto.toFixed(2)} 7%:${s.mwst_7.toFixed(2)} 19%:${s.mwst_19.toFixed(2)}` : '');
      sCells.push('');
    }
    sCells.push(data.summary.total.brutto.toFixed(2));
    lines.push(sCells.join(';'));

    const blob = new Blob(['﻿' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wg-tagesumsatz-${selectedStore?.store_number ?? 'store'}-${periode.min}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5 w-full">
      {/* Intro */}
      <div className="card">
        <div className="card-body flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
            <Icon name="reports" size={20} />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Warengruppen-Tagesumsatz
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Klassischer Tages-Bericht: pro Tag eine Zeile, pro Warengruppe Spalten
              (€ und %-Anteil). Wochen-Subsummen und Monats-Summary mit MwSt-Aufteilung
              (Brutto / Netto / 7% / 19%).
            </p>
          </div>
          <button onClick={exportCsv} disabled={!data} className="btn-secondary">
            <Icon name="download" size={14} />
            <span>CSV</span>
          </button>
        </div>
      </div>

      {/* Filter-Bar */}
      <div className="card card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 mb-1">
              Filiale
            </label>
            <select
              value={storeId ?? ''}
              onChange={(e) => selectStore(Number(e.target.value))}
              className="input"
            >
              {availableStores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.store_number} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <DateRangePicker
            label="Zeitraum"
            value={periode}
            onChange={(v) => {
              const next = {
                min: v.min ?? periode.min,
                max: v.max ?? periode.max,
              };
              setPeriode(next);
              useFilterStore
                .getState()
                .setRangeFilter('datum', next.min, next.max);
            }}
          />
          <div className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {selectedStore && (
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {selectedStore.name}
              </span>
            )}
            {periodTitle && <span className="block text-xs">{periodTitle}</span>}
          </div>
        </div>
      </div>

      {/* Bericht-Tabelle */}
      {isLoading ? (
        <div className="card card-body">
          <TableSkeleton rows={20} />
        </div>
      ) : !data || data.warengruppen.length === 0 ? (
        <div className="card card-body py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-400 flex items-center justify-center mx-auto mb-3">
            <Icon name="reports" size={22} />
          </div>
          <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Keine Daten für diese Filiale im gewählten Zeitraum
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="card-header">
            <div>
              <h3 className="card-title">
                {selectedStore?.name} — {periodTitle}
              </h3>
              <p className="card-subtitle">
                {data.warengruppen.length} Warengruppen · {data.days.length} Tage ·
                Gesamt {fmtCurrency(data.summary.total.brutto)} (Brutto)
              </p>
            </div>
          </div>
          <div className="overflow-auto" style={{ maxHeight: '75vh' }}>
            <table className="text-xs border-collapse w-max">
              <thead className="sticky top-0 z-20 bg-white dark:bg-slate-900">
                <tr className="bg-slate-50/80 dark:bg-slate-800/60 backdrop-blur">
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-left text-2xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 border-b-2 border-slate-200 dark:border-slate-700 sticky left-0 z-30 bg-slate-50/95 dark:bg-slate-800/90 backdrop-blur"
                    style={{ minWidth: 110 }}
                  >
                    Datum
                  </th>
                  {data.warengruppen.map((wg) => {
                    const isTop = wg.id === topWgId;
                    const isInTop3 = topWgIds.has(wg.id);
                    return (
                      <th
                        key={wg.id}
                        colSpan={2}
                        className={`px-2 py-1.5 text-center font-medium text-slate-700 dark:text-slate-200 border-b-2 border-r border-slate-200 dark:border-slate-700 ${
                          isTop ? 'bg-brand-100/50 dark:bg-brand-900/30' : ''
                        }`}
                        title={`${wg.name} (WG ${wg.id})${
                          isTop ? ' — Top-Warengruppe der Periode' : ''
                        }`}
                      >
                        <div
                          className={`text-2xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 ${
                            isTop
                              ? 'text-brand-700 dark:text-brand-300'
                              : 'text-brand-600 dark:text-brand-400'
                          }`}
                        >
                          {isTop && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-brand-500"
                              title="Top der Periode"
                            />
                          )}
                          WG.{wg.id}
                        </div>
                        <div
                          className={`truncate text-2xs font-normal ${
                            isInTop3
                              ? 'text-slate-700 dark:text-slate-300'
                              : 'text-slate-500 dark:text-slate-400'
                          }`}
                          style={{ maxWidth: 130 }}
                        >
                          {wg.name}
                        </div>
                      </th>
                    );
                  })}
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-right text-2xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 border-b-2 border-l-2 border-slate-300 dark:border-slate-600"
                    style={{ minWidth: 80 }}
                    title="Anzahl Bons des Tages"
                  >
                    Kunden
                  </th>
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-right text-2xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 border-b-2 border-slate-200 dark:border-slate-700"
                    style={{ minWidth: 80 }}
                    title="Tagesumsatz / Anzahl Bons"
                  >
                    Ø-Bon
                  </th>
                  <th
                    rowSpan={2}
                    className="px-3 py-2 text-right text-2xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 border-b-2 border-slate-200 dark:border-slate-700 bg-brand-50/40 dark:bg-brand-950/30"
                    style={{ minWidth: 110 }}
                  >
                    Tages­umsatz
                  </th>
                </tr>
                <tr>
                  {data.warengruppen.map((wg) => (
                    <PairHead key={wg.id} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.days.map((d, idx) => {
                  const w = weekBoundaries.get(idx);
                  const empty = d.tag_total === 0;
                  return (
                    <FragmentRow
                      key={d.datum}
                      day={d}
                      empty={empty}
                      warengruppen={data.warengruppen}
                      week={w}
                      dayMaxWgId={dayMaxWg.get(d.datum) ?? ''}
                      weekMaxWgId={w ? weekMaxWg.get(`${w.jahr}-${w.kw}`) ?? '' : ''}
                      topWgId={topWgId}
                    />
                  );
                })}
                {/* Monats-Summary */}
                <tr className="bg-brand-50/60 dark:bg-brand-950/40 border-t-2 border-brand-300 dark:border-brand-800">
                  <td
                    className="px-2 py-1.5 font-bold text-slate-900 dark:text-slate-100 sticky left-0 z-10 bg-brand-50/95 dark:bg-brand-950/60 border-r border-slate-200 dark:border-slate-700 align-top"
                  >
                    {periodTitle}
                  </td>
                  {data.warengruppen.map((wg) => {
                    const s = data.summary.wg_values[wg.id];
                    const isTop = wg.id === topWgId;
                    if (!s)
                      return (
                        <td
                          key={wg.id}
                          colSpan={2}
                          className="px-2 py-1.5 border-r border-slate-200 dark:border-slate-700"
                        />
                      );
                    return (
                      <td
                        key={wg.id}
                        colSpan={2}
                        className={`px-2 py-1.5 border-r border-slate-200 dark:border-slate-700 tabular-nums align-top text-2xs leading-snug ${
                          isTop ? 'bg-brand-200/50 dark:bg-brand-900/50' : ''
                        }`}
                        title={isTop ? 'Stärkste Warengruppe der Periode' : undefined}
                      >
                        <div>
                          <span className="text-slate-500">B:</span>{' '}
                          <span
                            className={
                              isTop
                                ? 'font-bold text-brand-700 dark:text-brand-300'
                                : 'font-semibold'
                            }
                          >
                            {fmtCurrency(s.brutto)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">N:</span>{' '}
                          {fmtCurrency(s.netto)}
                        </div>
                        {s.mwst_7 > 0 && (
                          <div>
                            <span className="text-slate-500">7%:</span>{' '}
                            {fmtCurrency(s.mwst_7)}
                          </div>
                        )}
                        {s.mwst_19 > 0 && (
                          <div>
                            <span className="text-slate-500">19%:</span>{' '}
                            {fmtCurrency(s.mwst_19)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold border-l-2 border-slate-300 dark:border-slate-600 align-top">
                    {fmtNumber(data.summary.anz_kunden, 0)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold align-top">
                    {data.summary.anz_kunden > 0
                      ? fmtNumber(data.summary.umsatz_pro_kunde, 2)
                      : ''}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold align-top text-2xs leading-snug">
                    <div>
                      <span className="text-slate-500">B:</span>{' '}
                      {fmtCurrency(data.summary.total.brutto)}
                    </div>
                    <div>
                      <span className="text-slate-500">N:</span>{' '}
                      {fmtCurrency(data.summary.total.netto)}
                    </div>
                    <div>
                      <span className="text-slate-500">MwSt:</span>{' '}
                      {fmtCurrency(
                        data.summary.total.mwst_7 + data.summary.total.mwst_19,
                      )}
                    </div>
                    {data.summary.total.mwst_7 > 0 && (
                      <div className="text-slate-600 dark:text-slate-400">
                        <span className="text-slate-500">7%:</span>{' '}
                        {fmtCurrency(data.summary.total.mwst_7)}
                      </div>
                    )}
                    {data.summary.total.mwst_19 > 0 && (
                      <div className="text-slate-600 dark:text-slate-400">
                        <span className="text-slate-500">19%:</span>{' '}
                        {fmtCurrency(data.summary.total.mwst_19)}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PairHead() {
  return (
    <>
      <th className="px-2 py-1 text-right text-2xs font-medium text-slate-500 dark:text-slate-400 border-b border-r border-slate-200 dark:border-slate-700">
        €
      </th>
      <th className="px-2 py-1 text-right text-2xs font-medium text-slate-500 dark:text-slate-400 border-b border-r border-slate-200 dark:border-slate-700">
        %
      </th>
    </>
  );
}

function FragmentRow({
  day,
  empty,
  warengruppen,
  week,
  dayMaxWgId,
  weekMaxWgId,
  topWgId,
}: {
  day: {
    datum: string;
    wochentag: number;
    kw: number;
    wg_values: Record<string, { brutto: number; anteil_pct: number }>;
    tag_total: number;
    anz_kunden: number;
    umsatz_pro_kunde: number;
  };
  empty: boolean;
  warengruppen: Array<{ id: string; name: string }>;
  week?: {
    kw: number;
    week_total: number;
    week_netto: number;
    wg_values: Record<string, { brutto: number; anteil_pct: number }>;
    anz_kunden: number;
    umsatz_pro_kunde: number;
  };
  dayMaxWgId: string;
  weekMaxWgId: string;
  topWgId: string;
}) {
  const isWeekend = day.wochentag === 6 || day.wochentag === 7;
  return (
    <>
      <tr
        className={
          empty
            ? 'text-slate-300 dark:text-slate-700'
            : `hover:bg-brand-50/40 dark:hover:bg-brand-950/20 transition-colors ${
                isWeekend ? 'bg-slate-50/40 dark:bg-slate-800/20' : ''
              }`
        }
      >
        <td
          className={`px-3 py-1.5 font-medium border-b border-r border-slate-100 dark:border-slate-800 sticky left-0 z-10 whitespace-nowrap ${
            empty
              ? 'text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-900'
              : isWeekend
                ? 'text-slate-700 dark:text-slate-200 bg-slate-50/60 dark:bg-slate-800/30'
                : 'text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900'
          }`}
        >
          {fmtDateShort(day.datum)}
          <span
            className={`text-2xs ml-1.5 ${
              isWeekend
                ? 'text-brand-500 dark:text-brand-400 font-semibold'
                : 'text-slate-400'
            }`}
          >
            {WT_SHORT[day.wochentag] ?? ''}
          </span>
        </td>
        {warengruppen.map((wg) => {
          const v = day.wg_values[wg.id];
          const isDayMax = !empty && wg.id === dayMaxWgId;
          const isTopCol = wg.id === topWgId;
          return (
            <td
              key={wg.id}
              colSpan={2}
              className={`border-b border-r border-slate-100 dark:border-slate-800 p-0 ${
                isDayMax
                  ? 'bg-brand-100/50 dark:bg-brand-900/30'
                  : isTopCol
                    ? 'bg-brand-50/30 dark:bg-brand-950/20'
                    : ''
              }`}
              title={isDayMax ? 'Tages-Spitzenreiter' : undefined}
            >
              {v ? (
                <div className="flex">
                  <span
                    className={`flex-1 px-1.5 py-1 text-right tabular-nums ${
                      isDayMax
                        ? 'font-bold text-brand-700 dark:text-brand-300'
                        : ''
                    }`}
                  >
                    {fmtNumber(v.brutto, 2)}
                  </span>
                  <span
                    className={`w-11 px-1.5 py-1 text-right tabular-nums text-2xs ${
                      isDayMax
                        ? 'font-semibold text-brand-600 dark:text-brand-400'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {fmtPercent(v.anteil_pct, 1)}
                  </span>
                </div>
              ) : (
                <span className="block px-1.5 py-1 text-right text-slate-300 dark:text-slate-700">
                  –
                </span>
              )}
            </td>
          );
        })}
        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-300 border-b border-l-2 border-slate-100 dark:border-slate-800 dark:border-l-slate-700">
          {empty ? '' : fmtNumber(day.anz_kunden, 0)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800">
          {empty || day.anz_kunden === 0 ? '' : fmtNumber(day.umsatz_pro_kunde, 2)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-800 dark:text-slate-100 border-b border-slate-100 dark:border-slate-800 bg-brand-50/30 dark:bg-brand-950/10">
          {empty ? '' : fmtNumber(day.tag_total, 2)}
        </td>
      </tr>
      {week && (
        <tr className="bg-slate-100/80 dark:bg-slate-800/60 font-semibold border-y border-slate-200 dark:border-slate-700">
          <td className="px-2 py-1 sticky left-0 z-10 bg-slate-100/95 dark:bg-slate-800/80 border-r border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-3 rounded-full bg-brand-500/70" />
              KW {week.kw}
            </span>
          </td>
          {warengruppen.map((wg) => {
            const v = week.wg_values[wg.id];
            const isWeekMax = wg.id === weekMaxWgId;
            return (
              <td
                key={wg.id}
                colSpan={2}
                className={`border-r border-slate-200 dark:border-slate-700 p-0 ${
                  isWeekMax ? 'bg-brand-200/40 dark:bg-brand-900/40' : ''
                }`}
                title={isWeekMax ? 'Wochen-Spitzenreiter' : undefined}
              >
                {v ? (
                  <div className="flex">
                    <span
                      className={`flex-1 px-1.5 py-1 text-right tabular-nums ${
                        isWeekMax ? 'font-bold text-brand-700 dark:text-brand-300' : ''
                      }`}
                    >
                      {fmtNumber(v.brutto, 2)}
                    </span>
                    <span
                      className={`w-12 px-1.5 py-1 text-right tabular-nums text-2xs ${
                        isWeekMax
                          ? 'font-semibold text-brand-600 dark:text-brand-400'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {fmtPercent(v.anteil_pct, 1)}
                    </span>
                  </div>
                ) : null}
              </td>
            );
          })}
          <td className="px-2 py-1 text-right tabular-nums border-l-2 border-slate-300 dark:border-slate-600">
            {fmtNumber(week.anz_kunden, 0)}
          </td>
          <td className="px-2 py-1 text-right tabular-nums">
            {week.anz_kunden > 0 ? fmtNumber(week.umsatz_pro_kunde, 2) : ''}
          </td>
          <td className="px-2 py-1 text-right tabular-nums align-top text-2xs leading-snug">
            <div>
              <span className="text-slate-500">B:</span>{' '}
              <span className="font-semibold">{fmtNumber(week.week_total, 2)}</span>
            </div>
            <div>
              <span className="text-slate-500">N:</span>{' '}
              {fmtNumber(week.week_netto, 2)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
